// ** React Imports
import { React, Fragment, useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Avatar,
  AvatarGroup,
  Stepper,
  StepLabel,
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Menu,
  MenuItem,
  FormControl,
  FormLabel,
  FormControlLabel,
  FormGroup,
  Grid,
  Divider,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Popover,
} from "@mui/material";
import MuiStep from "@mui/material/Step";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineContent from "@mui/lab/TimelineContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import MuiTimeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { getStatusRules, STATUS_ORDER, STATUS_RULES, canTransition} from "./logic/StatusManager";

// ** Util Imports

import { hexToRGBA } from "./"
// ** Custom Component Imports
import CustomTextField from "../../components/mui/text-field";
import CustomChip from "../../components/mui/chip";

// ** Component Imports
import AddJobsiteDialog from "../Customers/component/AddJobsite";
import AddContactDialog from "../Customers/component/AddContact";
//import AddServiceDialog from "./component/AddService";
import DateRangeCalendar from "../Customers/component/DateRangeCalendar";

import AttachmentUploadDialog from "components/AttachmentUpload";

// ** Icon Imports
import Icon from "../../components/icon";
import InfoIcon from "@mui/icons-material/Info";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";

// ** Custom Components Imports
import StepperCustomDot from "../../components/Stepper/StepperCustomDot";
import CustomAvatar from "../../components/mui/avatar";
import CustomAutocomplete from "components/mui/autocomplete";
import OptionsMenu from "../../components/option-menu";

// ** Styled Component
import StepperWrapper from "../../components/styles/mui/stepper";

// ** Third Party Components
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDropzone } from "react-dropzone";
import dayjs from "dayjs";
import { format, set } from 'date-fns';
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";

// ** Other Imports
import { GetServiceRequestRaw, GetServiceRequests } from "./logic/GetServiceRequest";
import { useCreateRequestLogic } from "./logic/CreateRequestLogic";
import { GetJobsite } from "../Customers/logic/GetJobsites";
import { getToken } from "../../utils/auth";
import ServicesComponent from "./component/ServicesComponent";
import LinkedRecordsComponent from "./component/LinkedRecordsComponent";
import NotesComponent from "./component/NotesComponent";
import ConversionComponent from "./component/ConversionComponent";
import AssignToComponent from "screens/Assessments/component/AssignToComponent";
import { useIndexLogic } from "screens/Customers/logic/indexLogic";
import { useCustomerLogic } from "screens/Customers/logic/CustomerLogic";
// import AssignToDialog from "./component/AssignTo";
import PreAttachment from "./component/PreAttachment";
import AddCustomerDialog from "screens/Customers/component/AddCustomer";
import TagManagementDialog from "screens/Customers/component/TagManagement";
import { saveAs } from 'file-saver';
import { useUnsavedChanges } from "components/UnsavedChangesContext";
import _ from 'lodash';
import { DownloadAttachments } from "components/logic/DownloadAttachments";
import WorkRequirements from "screens/Assessments/component/WorkRequirements";
import AddBundle from "screens/Assessments/component/AddBundle";
import AddService from "screens/Assessments/component/AddService";
import { Assessment } from "@mui/icons-material";
import { useServiceLogic } from "screens/Services/logic/serviceLogic";

import AddServiceDialog from "screens/Assessments/component/AddService";
import AddBundleDialog from "screens/Assessments/component/AddBundle";
import { useWorkRequirementLogic } from "screens/WorkOrders/logic/work-requirement-logic";
import { useAssignToLogic } from "screens/Assessments/logic/AssignToLogic";
import { useServiceRequestPermission } from './permissions/RequestPermissions';
import AttachmentsComponent from "./component/AttachmentsComponent";
import { SendNotification } from "components/SendNotification";
import { GetEntityLinks, DeleteLinkRecord } from "./logic/LinkRecordLogic";


const stepsRequestDetails = [
  {
    icon: "tabler:user",
    title: "General Information",
  },
  {
    icon: "tabler:address-book",
    title: "Work Requirements",
  },
];

// Styled Timeline component
const Timeline = styled(MuiTimeline)({
  "& .MuiTimelineItem-root": {
    width: "100%",
    "&:before": {
      display: "none",
    },
  },
});

// ** Styled Components
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : theme.palette.common.white,
    minWidth: 220,
    maxWidth: 440,
    border: "1px solid #E1E4EA",
  },
}));

const Customersteps = [
  {
    icon: "tabler:user",
    title: "Profile Information",
  },
  {
    icon: "tabler:address-book",
    title: "Add Contact",
  },
  {
    icon: "tabler:align-box-bottom-center",
    title: "Add Jobsite",
  },
];
 const CustomChipWithDropdown = ({ tag, onSelect }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();

    const isUnclickable = ["Assessment", "Work Order", "Estimate"].includes(tag);

    const handleClick = (event) => {
      if (isUnclickable) return;
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleSelect = (selectedTag) => {
      onSelect(selectedTag);
      handleClose();
    };

    const statusColors = {
      "Under Review": "info",
      "Submitted": "success",
      "Completed": "success",
      "Assessment": "success",
      "Estimate": "success",
      "Work Order": "success",
      "On Hold": "warning",
      "Canceled by Customer": "error",
      "Closed": "error",
      "Canceled": "error",
    };
    
    const statuses = [
      "Under Review",
      "On Hold",
      "Completed",
      "Canceled",
      "Canceled by Customer",
    ];
    
    return (
      <Box>
        <CustomChip
          borderRadius="4px"
          size="small"
          skin="light"
          onClick={handleClick}
          color={statusColors[tag] || "secondary"}
          sx={{
            lineHeight: 1,
            py: 1,
            ...(isUnclickable && {
              pointerEvents: "none", 
              cursor: "default",
              "&:hover": {
                backgroundColor: "inherit !important",
                boxShadow: "none !important",
              },
              opacity: 1,
            }),
          }}
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FiberManualRecordIcon fontSize="small" />
              <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
                Status:
              </Typography>
              <Typography variant="caption" sx={{ fontSize: "0.8rem", fontWeight: 660 }}>
                {tag}
              </Typography>
            </Box>
          }
        />
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          disableAutoFocusItem   
          disableRestoreFocus
          PaperProps={{ sx: { width: 200 } }}
        >
          {statuses.map((status) => {
            const isSelected = tag === status;

            return (
              <MenuItem
                key={status}
                selected={isSelected}
                onClick={() => handleSelect(status)}
                sx={{
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                }}
              >
                {status}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    );
  };
const RequestDetail = () => {
  //#region Permissions
  const { ServiceRequestPermissionProps } = useServiceRequestPermission();
  const {
    hasConvertToPermission,
    hasStatusUpdatePermission,
  } = ServiceRequestPermissionProps;
  //#endregion Permissions

  // ** Hooks and Vars
  const MySwal = withReactContent(Swal);

  //#region States
  const contentHeightFixed = false;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState("1");
  // const [checked, setChecked] = useState([]);
 // const [openServiceDlg, setOpenServiceDlg] = useState(false);
  const [contactName, setContactName] = useState([]);
  //const [openService, setOpenService] = useState(false);
  const [sortTimeline, setSortTimeline] = useState(true);
  const [data, setData] = useState([]);
  const [request, setRequest] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [jobsiteContacts, setJobsiteContacts] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState("Select");
  const [openScheduleAst, setOpenScheduleAst] = useState(false);
  const [schedulingReq, setSchedulingReq] = useState(false);
  // const [openAssignTo, setOpenAssignTo] = useState(false);
  const [flagList, setFlagList] = useState({
    isCustomerLoaded:false, 
    isJobsiteLoaded:false, 
    isContactsLoaded:false, 
    isPrefferedTimeLoaded:false,
    isPreferedDaysLoaded:false,
    isCompleteWorkByLoaded:false, 
    isTagsLoaded:false,
    isViewed:false
  });
  const [preferredTimes, setPreferredTimes] = useState([]);
  const [openUploadDlg2, setOpenUploadDlg2] = useState(false);
  // const [uploadFiles2, setUploadFiles2] = useState([]);
  // const [files2, setFiles2] = useState([])
  // const [btnLoader, setBtnLoader] = useState(false);
  // const [attachments, setAttachments] = useState([]);
  const [undoClicked, setUndoClicked] = useState(false);
  // const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  // const [groupedAttachments, setGroupedAttachments] = useState({});
  const [tabValue, setTabValue] = useState("1");
  // const [startDate, setStartDate] = useState(null);
  // const [endDate, setEndDate] = useState(new Date);
  const [status, setStatus] = useState('');
  const [conversionBannerVisible, setConversionBannerVisible] = useState(false);
  const [linkedFromEntityId, setLinkedFromEntityId] = useState(null);
  const [rules, setRules] = useState(
    { 
      canEdit: true,
      canDelete: true,
      canSubmit: true,
      nextStatuses: [],
      cannotGoBack: false
    });
  // const [customerRequests, setCustomerRequests] = useState([]);   
  //const [allRequests, setAllRequests] = useState([]);
  
  const { requestID } = useParams();   

  const handleOpenScheduleAstDlg = () => {
    setOpenScheduleAst(true);
  }

  const handleCloseScheduleAstDlg = () => {
    setOpenScheduleAst(false);
  }

  // const handleOpenAssignToDlg = () => {
  //   setOpenAssignTo(true);
  // }

  // const handleCloseAssignToDlg = () => {
  //   setOpenAssignTo(false);
  // }

  useEffect(() => {  
    if(requestID){
      fetchRequests(0, requestID, true);      
    }      
  }, [requestID]);

  
  const customerID_New = 0;
 
  const handleClose = () => {
    setAnchorEl(null);
  };
  const recordOptions = [];

 
  
  const { CreateRequestProps, WorkReqProps } = useCreateRequestLogic({handleClose,activeStep,setActiveStep,recordOptions,stepsRequestDetails,setLoading});
   
  const [openNewCustomerDlg, setOpenNewCustomerDlg] = useState(false);
  const {IndexProps} = useIndexLogic();
  const {fetchData, allCustomers} = IndexProps;

  const {CustomerProps} = useCustomerLogic({activeStep, setActiveStep,Customersteps,setOpenNewCustomerDlg, fetchData, allCustomers});

  const {setCustomer, customer, allJobsites, setAllJobsites,setIsCompleted, isCompleted, fetchContacts, contacts,
    jobsite,
    setJobsite,
    openNewJobsiteDlg,
    setOpenNewJobsiteDlg,
    handleOpenNewJobsiteDlg,
    handleCloseNewJobsiteDlg,
    handleJobsiteChange,
    handleOpenTagManagementDlg,
    //handleDeleteTag,
    handleOpenNewCustomerDlg,
    openTagManagementDlg,
    handleCloseTagManagementDlg,
    fetchJobsites
  } = CustomerProps;

  const [oldServiceRequest, setOldServiceRequest] = useState({});
  const [isChanged, setIsChanged] = useState(false);
  const { setHasUnsavedChanges, hasUnsavedChanges } = useUnsavedChanges();
  const [openUnsavedChangesDlg, setOpenUnsavedChangesDlg] = useState(false);
  const [unsavedDialogAction, setUnsavedDialogAction] = useState({
    action: '',
    tabValue: '',
  });
  const handleOpenUnsavedChangesDlg = (action, tabValue) => {
    if(action === 'tabChange'){
      setUnsavedDialogAction({
        action: action,
        tabValue: tabValue,
      });
    }else{
      setUnsavedDialogAction({
        action: action,
        tabValue: '',
      });
    }
    setOpenUnsavedChangesDlg(true); 
  }
  const handleCloseUnsavedChangesDlg = () => setOpenUnsavedChangesDlg(false);
  const handleDiscardChanges = () => { 
    if(unsavedDialogAction.action === 'tabChange'){
      setOpenUnsavedChangesDlg(false); 
      setTabValue(unsavedDialogAction.tabValue)
    } 
    else if(unsavedDialogAction.action === 'upload'){
  setOpenUnsavedChangesDlg(false); 
    setFiles([]);
    setUploadFiles([]);
    setOpenUploadDlg2(false); 
}
    else{
      setOpenUnsavedChangesDlg(false); 
      navigate('/requests')
    }
  }
  const [btnLoading, setButtonLoading] = useState(false);
  const [serviceList, setServiceList] = useState([]);

  //#endregion states

  const {
    handleDeleteRecord,
    handleCancelAddRecord,
    handleAddRecord,
    handleClickAddRecordSection,
    handleFormat, 
    handleOpenNewContactDlg,
    handleCloseServiceDlg,
    //handleOpenServiceDlg,
    isServiceEmpty,
    setIsServiceEmpty,
    openAddBundle,
    setOpenAddBundle,
    isBundleEmpty,
    setIsBundleEmpty,
    //handleOpenBundleDlg,
    handleCloseBundleDlg,
    handleSave,
    // handleCloseNewJobsiteDlg,
    handleCloseNewCustomerDlg,
    //handleOpenNewCustomerDlg,
    handleBack,
    handleServiceNext,
    handleNext,
    formats,
    ddlArray,
    notesSort,
    workReqIsEmpty,
    openService,
    openAddService,
    linkedRecordIsEmpty,
    linkedRecords,
    newRecord,
    isAddRecordOpen,
    //openNewCustomerDlg,
    // openNewJobsiteDlg,
    openNewContactDlg,
    //setOpenNewCustomerDlg,
    // setOpenNewJobsiteDlg,
    //setCustomer, 
    //setAllJobsites, 
    //allCustomers, 
    setOpenNewContactDlg,
    NProgress,
    //StepperHeaderContainer,
    StepJobsite,
    columnsAddContact,
    quickViewOpen,
    //contacts,
    // jobsite,
    // setJobsite,
    propertyAreaList,
    alertList,
    //customer,
    errors, 
    isValidPhone, 
    phoneError,  
    theme,
    searchContactText,
    searchJobsiteText,
    filteredContactRows,
    paginationModelAddContact,
    //allJobsites,
    //activeStep,
    openUpdateContactDlg,
    toast,
    contactID,      
    allContacts,
    propertyArea,
    areaMeasurement,
    activeStepJobsite,
    jobsiteContent,
    clearPropertyArea,
    fetchPropertyArea,
    clearAreaMeasurement,
    setActiveStepJobsite,
    setPropertyArea,
    setAreaMeasurement,
    navigate,
    // handleOpenNewJobsiteDlg,
    setContactID,
    handleChange,
    setAlertList,
    handleQuickViewClose,
    // handleJobsiteChange,
    handleJobsiteDelete,
    handleJobsiteEdit,
    handleQuickViewOpen,
    handlePhoneFieldChange,
    handleSetPrimary,
    handleDeletePhoneField,
    handleAddPhoneField,
    handleClearContactSearch,
    handleContactSearchChange,
    handleClearJobsiteSearch,
    handleJobsiteSearchChange,
    handleContactPaginationModel,
    handleCloseUpdateDlg, 
    //fetchContacts, 
    files,
    setFiles,
    handleOpenBulkImportContactDlg,
    handleOpenBulkImportJobsiteDlg,
    contact,
    handleContactChange,
    errorContacts,
    handleContactPhoneFieldChange,
    handleSetPrimaryContact,
    handleDeletePhoneFieldContact,
    handleAddPhoneFieldContact,
    isValidContactPhone,
    contactPhoneError,
    //handleClose,
    // fetchJobsites,
    //openTagManagementDlg,
   // handleCloseTagManagementDlg,   
    handleDeleteTag,
    handleAddTag,
    serviceRequest,
    setServiceRequest,
    Tag,
    setTag,
    resetRequest,
    handleCloseContactDialog,
    openUploadDlg,
    handleOpenUploadDlg,
    uploadFiles,
    setUploadFiles,
    sf,
    f,
    handleCloseUploadDlg,
    fetchWorkRequirements,
    workRequirementsList,
    fetchWorkRequirementsSub,
    workRequirementsListSub,
    handleWorkRequirementToggle,
    handleEditService,
    handleDeleteService,
    services,
    handleDownloadAttachment,
    handleDeleteAttachment,
    attachmentList,
    //fetchData,
    serviceContent, 
    setServiceContent,
    ResetServiceContent,
    setNotesSort,
    handleCustomerDropdownChange,
    requestErrors,
    handleJobsiteDropdownChange,
    handleContactDropdownChange,
    handleCompleteWorkByDateChange,
    SaveService,
    //validateRequestField,
    handleOpenNewPropertyAreaDlg,
    handleDeleteFile,
    setOpenService,
    setRequestErrors
  } = CreateRequestProps;

   const [initialServiceRequest, setInitialServiceRequest] = useState(serviceRequest);
  const {  
    AssignToProps
  } = useAssignToLogic({serviceRequest, setLoading, requestID, requestType: "Request", navigate});

  const {    
    filteredEmployees,     
    assignedUserIds, 
  } = AssignToProps;

  // Work Requirememt section
  
    const {
      AddServiceProps,
      AddBundleProps,
      WorkRequirementsProps,
      AttachmentProps
    } = useWorkRequirementLogic({requestID, navigate,toast, setLoading,serviceRequest});

    const {
      handleOpenBundleDlg,
      handleOpenServiceDlg,
      workRequirementList,    
    } = WorkRequirementsProps;
  
    /////end of work Requirement

  const daysMapping = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const timeMapping = { 1: "Anytime", 2: "Morning", 3: "Afternoon", 4: "Evening" };

  const allDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const allTimes = ["Anytime", "Morning", "Afternoon", "Evening"];

  const dayToNumberMap = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 0,
  };

  const numberToDayMap = Object.keys(dayToNumberMap).reduce((acc, day) => {
    acc[dayToNumberMap[day]] = day;
    return acc;
  }, {});

  const parseAvailabilityDays = (daysString) => {
    try {
      const daysArray = JSON.parse(daysString);
      // Ensure that only numbers are kept in the array
      return daysArray.filter((item) => typeof item === 'number');
    } catch (error) {
      //console.error('Error parsing availabilityDays:', error,daysString );
      return [];
    }
  };  
  
  const handleNavigation = (to) => {   
    if(isChanged){
       handleOpenUnsavedChangesDlg('navigate');

       
      // MySwal.fire({
      //   title: "Are you sure?",
      //   html: "This page has unsaved changes",
      //   showCancelButton: true,
      //   confirmButtonColor: "#23A26D",
      //   cancelButtonColor: "#d33",
      //   confirmButtonText: "Discard Changes",
      //   cancelButtonText: "Cancel",
      //   allowEscapeKey: true,
      // }).then((result) => {
      //   if (result.isConfirmed) {
      //     navigate(-1);
      //     setHasUnsavedChanges(false);
      //   }
      // });
    }else{
      navigate(to);
    }
  };

  useEffect(() => {
    const isEqual = _.isEqual(oldServiceRequest, serviceRequest); // Deep compare OldEmp and employee
    setIsChanged(!isEqual); 
    setHasUnsavedChanges(!isEqual);
  }, [oldServiceRequest, serviceRequest]);

  

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
  
   const handleBackButton = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();

         handleOpenUnsavedChangesDlg('navigate');

        // MySwal.fire({
        //   title: "Are you sure?",
        //   html: "This page has unsaved changes",
         
        //   showCancelButton: true,
        //   confirmButtonColor: "#23A26D",
        //   cancelButtonColor: "#d33",
        //   confirmButtonText:"Discard Changes",
        //   cancelButtonText: "Cancel",
        //   allowEscapeKey: true,
        // }).then((result) => {
        //   if (result.isConfirmed) {
        //     navigate(-1);
        //     setHasUnsavedChanges(false);
        //   }
        // });
      }else{
        navigate('/requests');
      }
    };
  
    window.history.pushState(null, '', window.location.href);
  
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handleBackButton);
  
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [hasUnsavedChanges]);

  //  useEffect(() => {
  //       console.log('initialServiceRequest', initialServiceRequest)
  //     }, [initialServiceRequest])

  const fetchRequests = async (customerID_New,requestID, loading = true) => {    
      try {
        setLoading(loading);
        let requests = await GetServiceRequests(customerID_New, requestID);
        let RequestRaw = await GetServiceRequestRaw(customerID_New, requestID);

        const processRequestData = (rawRequest) => {        
          return {
            ...rawRequest,
            preferredDays: JSON.parse(rawRequest.preferred_days || "[]").map(
              (day) => daysMapping[day - 1]
            ),
            preferredTimes: JSON.parse(rawRequest.preferred_time || "[]").map(
              (time) => timeMapping[time]
            ),
            completeWorkBy: dayjs(rawRequest.completeWorkBy_original).format("MM/DD/YYYY"),
          };
        };
        
        // Process and set the updated request object
        const rawRequest = requests[0];
        const updatedRequest = processRequestData(rawRequest);
        const newRequest = RequestRaw.result[0];

        setRequest(updatedRequest);
        setServiceRequest(newRequest);
        setOldServiceRequest(newRequest);
        if (!undoClicked) {
          setInitialServiceRequest(newRequest);
        }
      } catch (error) {
        console.error("Error fetching Requests data:", error);
      } finally {
        setLoading(false);
      }
  };

  useEffect(()=>{
    if(flagList.isCustomerLoaded && flagList.isJobsiteLoaded, flagList.isContactsLoaded){ 
      const timer = setTimeout(() => {        
        setLoading(false); 
        handleIsViewed();
      }, 1000);
      return () => clearTimeout(timer);                
    }
  },[flagList.isCustomerLoaded && flagList.isJobsiteLoaded, flagList.isContactsLoaded])

  useEffect(() => { 
    if(serviceRequest?.customerID > 0){
      fetchJobsites2(serviceRequest?.customerID); 
    }         
  }, [serviceRequest?.customerID]);

  useEffect(()=>{
    if(serviceRequest?.requestID > 0){
      setRules(getStatusRules(serviceRequest?.status));
      setStatus(serviceRequest?.status);      
    }    
  },[serviceRequest?.requestID]);

  useEffect(()=>{
    if(flagList.isViewed && serviceRequest?.isViewed){

      const SaveStatus = async () => {
        const result = await SaveService();
      
        if (result) {
          fetchRequests(0, requestID, false);
          setStatus(serviceRequest.status);
          setRules(getStatusRules(serviceRequest?.status));
          toast.success("Status changed to "+serviceRequest?.status, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });        
        } else {
          toast.error("Status update failed.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
              theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      }
      
      SaveStatus();

      //fetchRequests(0, requestID);
      setFlagList((prev)=>({
        ...prev, "isViewed":false
      }));
    }
  },[flagList.isViewed]);

  const handleIsViewed = () => {
    if(!serviceRequest?.isViewed && (serviceRequest?.status === ('Submitted' || 'Not Viewed'))){
      setServiceRequest((prevState) => ({
        ...prevState,
        status: 'Under Review',
        isViewed : true
      }));
      setFlagList((prev)=>({
        ...prev, "isViewed":true
      }));
      setStatus('Under Review');   
    }
  };

  const handleServiceRequestChange = (key, value) => {
    setServiceRequest((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }

  const handleTimeChange = (timeValue, isSelected) => {
    try {
      const timeKey = Number(Object.keys(timeMapping).find((key) => timeMapping[key] === timeValue));
      let updatedTimes = JSON.parse(serviceRequest.availabilityTime || "[]");
  
      if (isSelected) {
        if (timeKey === 1) {
          // If "Anytime" is selected, replace all times with [1, 2, 3, 4]
          updatedTimes = [1, 2, 3, 4];
        } else {
          // Add the specific time
          updatedTimes = [...new Set([...updatedTimes, timeKey])];
          // Remove "Anytime" if another time is selected
          updatedTimes = updatedTimes.filter((t) => t !== 1);
  
          // Automatically check "Anytime" if all 2, 3, 4 are selected
          if (updatedTimes.includes(2) && updatedTimes.includes(3) && updatedTimes.includes(4)) {
            updatedTimes = [1, 2, 3, 4];
          }
        }
      } else {
        // Remove the time from the list
        updatedTimes = updatedTimes.filter((t) => t !== timeKey);
  
        // If "Anytime" is being deselected, clear all times
        if (timeKey === 1) {
          updatedTimes = [];
        } else {
          // Uncheck "Anytime" if any specific time is deselected
          updatedTimes = updatedTimes.filter((t) => t !== 1);
        }
      }
  
      // Update the serviceRequest state
      setServiceRequest((prevState) => ({
        ...prevState,
        availabilityTime: JSON.stringify(updatedTimes),
      }));
        
    } catch (e) {
      console.log("Error in handleTimeChange:", e);
    }
  };
  
  const handleDayChange = (day) => {
    // Parse the availabilityDays string into an actual array of numbers
    let updatedDays = serviceRequest.availabilityDays
      ? parseAvailabilityDays(serviceRequest.availabilityDays || '[]')
      : [];

    // Get the numeric value of the selected day (e.g., "Mon" -> 1)
    const dayNumber = dayToNumberMap[day];

    // Toggle the day in the list
    if (updatedDays.includes(dayNumber)) {
      // If the day is already selected, remove it
      updatedDays = updatedDays.filter((d) => d !== dayNumber);
    } else {
      // If the day is not selected, add it
      updatedDays.push(dayNumber);
    }

    // Update the serviceRequest state, storing only numbers
    setServiceRequest((prevState) => ({
      ...prevState,
      availabilityDays: JSON.stringify(updatedDays), // Store as a clean stringified array of numbers
    }));
  };

  const handleStatusChange = (key,value) => {    
    if (canTransition(status, value)) {
      setServiceRequest((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    } else {
      toast.error(`You cannot move from ${status} to ${value}.`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
    }       
    
  }
 
  useEffect(() => {
    if (requestID) {
      fetchLinks();
    }
  }, [requestID]);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      // debugger;
      const linkedEntities = await GetEntityLinks(requestID);      
      if (linkedEntities && linkedEntities.length > 0) {
        const linkedEntity = linkedEntities.find((entity => entity.toEntityId
 || entity.toEntityId
 == requestID));
        if (linkedEntity) {
          setLinkedFromEntityId(linkedEntity.fromEntityId);
        }}
    } catch(error){
      console.log(error);
    } finally {
      setLoading(false);
    }
  }
  const availabilityDaysArray = parseAvailabilityDays(serviceRequest.availabilityDays || "[]");

  useEffect(()=>{
    if(serviceRequest?.jobSiteID  || serviceRequest?.jobSiteID){
      // console.log('Fetching propertyarea of jobsiteID : ', serviceRequest?.jobSiteID)
      setJobsite(allJobsites.filter(x => x.jobsiteID === serviceRequest?.jobSiteID)[0]);
      fetchPropertyArea(serviceRequest?.jobSiteID);
      setFlagList((prev)=>({
        ...prev, "isJobsiteLoaded":true
      })); 
    }
  },[allJobsites]);

  useEffect(()=> {
    setCustomer(allCustomers.filter(x => x.customerID === serviceRequest?.customerID)[0]); 
    setIsCompleted(true);  
    //fetchWorkRequirements(requestID);     
    setFlagList((prev)=>({
      ...prev, "isCustomerLoaded":true
    }));     
  },[allCustomers,serviceRequest?.customerID])

  const fetchJobsites2 = async (customerID) => {
    if(customerID) {
      try {
        let jobs = await GetJobsite(customerID, 0, navigate);  
        setAllJobsites(jobs);

      } catch (error) {
        console.error("Error fetching Jobsite data:", error);
      }
    }
  };

  useEffect(() => {
    if (serviceRequest?.customerID) {      
      fetchContacts(serviceRequest?.customerID);
      setFlagList((prev)=>({
        ...prev, "isContactsLoaded":true
      })); 
    }    
  }, [serviceRequest?.customerID]);
  
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
    setOpenService([]);
  };

  const validateRequestField =() =>{
    let tempErrors = {};
    
      if (!serviceRequest.requestTitle) 
        tempErrors.requestTitle = 'Request Title is required';      

      if (!serviceRequest.customerID) 
          tempErrors.customerID = 'Customer is required';
  
      if (!serviceRequest.jobSiteID || !allJobsites?.some((jobsite) => jobsite.jobsiteID === serviceRequest?.jobSiteID)) 
          tempErrors.jobSiteID = 'Jobsite is required';
  
      if (!serviceRequest.jobsiteContactID || !contacts?.some((contact) => contact.id === serviceRequest?.jobsiteContactID)) 
          tempErrors.jobsiteContactID = 'Jobsite Contact is required';
    
    setRequestErrors(tempErrors);  
    return (Object.keys(tempErrors).length === 0); 
  }

   const handleUndo = async () => {
    const newValue = oldServiceRequest;
    setServiceRequest(newValue);

    const result = await SaveService(newValue);
    if(!result.isError){
      fetchRequests(0, requestID, true);
    }
  };
  const handleSaveServiceRequest = async () => {
    if (!validateRequestField()) {
      return;
    }    
    setButtonLoading(true);    
    try {
      const result = await SaveService();
      
      if (result) {
        SendNotification(navigate);
        fetchRequests(0, requestID, true);
        setStatus(serviceRequest.status);
        setRules(getStatusRules(serviceRequest?.status));
         if (!undoClicked) {
        toast.success(
          ({}) => (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography sx={{ flex: 1 }}>
                Service updated successfully.
              </Typography>
              <Button
                variant="contained"
                color="main"
                size="small"
                sx={{
                  mr: 4,
                  borderRadius: "20px", 
                  minWidth: "64px", 
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleUndo();
                }}
              >
                Undo
              </Button>
            </Box>
          ),

          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          }
        );  
      }      
      } else {
        toast.error("Service update failed.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
      
    } catch (e) {
      console.error(e);
    } finally {
      setButtonLoading(false);          
    }
  };  

  function formatDateSubtractDays(date, days) {
    // Create a copy of the date object to avoid mutating the original one
    const resultDate = new Date(date);
    
    // Subtract the specified number of days
    resultDate.setDate(resultDate.getDate() - days);
    
    // Extract year, month, and day from the resultDate
    const year = resultDate.getFullYear();
    const month = (resultDate.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed
    const day = resultDate.getDate().toString().padStart(2, '0');
    
    // Return the date in yyyy-mm-dd format
    return `${year}-${month}-${day}`;
  }

  // const handleDateRange = (startDate, endDate) => {
  //   setStartDate(startDate);
  //   setEndDate(endDate);
  // }
  
  const handlePageBack = () => {
    handleNavigation('/requests');    
  }
useEffect(() => {
  if (serviceRequest?.status) {
    const convertedStatuses = [
      'Assessment',
      'Estimate', 
      'Work Order',
      'Converted to Assessment',
      'Converted to Estimate',
      'Converted to Work Order'
    ];
    
    if (convertedStatuses.includes(serviceRequest.status)) {
      setConversionBannerVisible(true);
    }
    if (linkedFromEntityId) {
      console.log('Request is linked from entity:', linkedFromEntityId);
    }
  }
}, [serviceRequest?.status, linkedFromEntityId]);
const getEntityInfo = (status, entityId) => {
  let entityType = '';
  let prefix = '';
  let route = '';
  
  switch(status) {
    case 'Assessment':
    case 'Converted to Assessment':
      entityType = 'Assessment';
      prefix = 'AST';
      route = `/assessments/detail/${entityId}`;
      break;
    case 'Estimate':
    case 'Converted to Estimate':
      entityType = 'Estimate';
      prefix = 'EST';
      route = `/estimates/detail/${entityId}`;
      break;
    case 'Work Order':
    case 'Converted to Work Order':
      entityType = 'WorkOrder';
      prefix = 'WO';
      route = `/work-orders/detail/${entityId}`;
      break;
    case 'Request':
    default:
      entityType = 'Request';
      prefix = 'REQ';
      route = `/requests/detail/${entityId}`;
      break;
  }
  
  return { entityType, prefix, route };
};
  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Requests"}
      showIcon={true}
      NavURL={handlePageBack}
    >
      <Card sx={{ width: "100%", height: "100%", position: "relative" }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                gap: { xs: 2, md: 0 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "flex-start", md: "center" },
                  gap: { xs: 1, md: 2 },
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  REQ-{serviceRequest?.requestID}
                </Typography>
                <HtmlTooltip
                  title={
                    <Box sx={{ p: (4, 2) }}>
                      <Box>
                        <Typography
                          color="text.primary"
                          fontSize="small"
                          fontWeight="600"
                          mb={2}
                        >
                          Customer Details
                        </Typography>
                        <Box sx={{ display: "flex", gap: 20 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {customer?.customerType === "Business" ? (
                              <Typography
                                color="text.secondary"
                                fontSize="small"
                              >
                                Company Name
                              </Typography>
                            ) : (
                              <Typography
                                color="text.secondary"
                                fontSize="small"
                              >
                                Customer Name
                              </Typography>
                            )}
                            <Typography color="text.secondary" fontSize="small">
                              Email
                            </Typography>
                            <Typography color="text.secondary" fontSize="small">
                              Phone
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {customer?.customerType === "Business" ? (
                              <Typography fontSize="small">
                                {customer?.companyName}
                              </Typography>
                            ) : (
                              <Typography fontSize="small">
                                {serviceRequest?.customerName}
                              </Typography>
                            )}
                            <Typography fontSize="small">
                              {customer?.email}
                            </Typography>
                            <Typography fontSize="small">
                              {customer?.phoneNumbers[0]?.phoneNo}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  }
                >
                  <Box sx={{}}>
                    <CustomChip
                      sx={{ cursor: "pointer" }}
                      label={
                        <Typography
                          variant="body1"
                          color="primary"
                          sx={{ maxWidth: "200px", overflow: "hidden" }}
                        >
                          {serviceRequest?.requestTitle}
                        </Typography>
                      }
                      color="secondary"
                      skin={theme.palette.mode === "dark" ? "dark" : "light"}
                      icon={<Icon icon="tabler:building" />}
                    />
                  </Box>
                </HtmlTooltip>
                <CustomChip label="Internal" color="info" skin="light" />
               <CustomChipWithDropdown
                  tag={serviceRequest?.status}
                  onSelect={(selectedTag) => {
                    handleStatusChange('status', selectedTag);
                  }}
                />
                <AvatarGroup
                  className="pull-up"
                  max={4}
                  sx={{
                    "& .MuiAvatar-root": { width: 25, height: 25 },
                    "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                  }}
                >
                  {filteredEmployees &&
                    filteredEmployees
                      .filter((employee) =>
                        assignedUserIds.includes(employee.employeeID)
                      )
                      .map((employee, index) => {
                        const employeeFullName = `${employee.firstName} ${employee.lastName}`;
                        return (
                          <Tooltip
                            key={employee.employeeID}
                            title={employeeFullName}
                          >
                            <Avatar
                              src={
                                employee.avatarUrl ||
                                `/images/avatars/${index + 1}.png`
                              }
                              alt={employeeFullName}
                              sx={{ width: 25, height: 25 }}
                            />
                          </Tooltip>
                        );
                      })}
                </AvatarGroup>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* <Button variant='outlined' color='main' onClick={handleOpenAssignToDlg}>Assign To</Button> */}
                <Button
                  variant="contained"
                  color="main"
                  onClick={() => handleSaveServiceRequest()}
                  disabled={btnLoading || !isChanged}
                >
                  {btnLoading ? <CircularProgress size={24} /> : "Save Changes"}
                </Button>
                <ConversionComponent
                  handleOpenSchedule={handleOpenScheduleAstDlg}
                  schedulingReq={schedulingReq}
                  setSchedulingReq={setSchedulingReq}
                  originalRequestID={requestID}
                  openScheduleAst={openScheduleAst}
                  handleCloseScheduleAstDlg={handleCloseScheduleAstDlg}
                  setLoading={setLoading}
                  module="Request"
                  rules={rules}
                  hasConvertToPermission={hasConvertToPermission}
                />
              </Box>
            </Box>
          }
        />
        {conversionBannerVisible && serviceRequest?.status && (
        <Box
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(251,188,4,0.15)' : 'rgb(255, 224, 100)',
            border: `1px solid ${theme.palette.mode === 'dark' ? '#b38f00' : 'rgb(255, 224, 100)'}`,
            borderRadius: 1,
            padding: 4,
            margin: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Icon icon="tabler:info-circle" color={theme.palette.mode === 'dark' ? 'rgb(255, 224, 100)' : theme.palette.primary.main} />
          <Typography
            variant="h6"
            sx={{ color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.primary.main }}
          >
            This has been converted into {serviceRequest.status === 'Work Order' ? 'a' : 'an'} <strong>{serviceRequest.status}</strong>, and you can no longer make changes.
            {linkedFromEntityId && (
              <span>
                {' '}Click here to view{' '}
                <Link
                  to={getEntityInfo(serviceRequest.status, linkedFromEntityId).route}
                  style={{
                    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.primary.main,
                    textDecoration: 'underline',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(getEntityInfo(serviceRequest.status, linkedFromEntityId).route);
                  }}
                >
                  {getEntityInfo(serviceRequest.status, linkedFromEntityId).prefix}-{linkedFromEntityId}
                </Link>
              </span>
            )}
          </Typography>
        </Box>
      )}
        <CardContent sx={{ position: "relative" }}>
          <TabContext value={value}>
            <TabList
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="false"
              aria-label="simple tabs example"
            >
              <Tab
                value="1"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      color:
                        theme.palette.mode === "dark"
                          ? value === "1"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Request Details
                    </Box>
                  </Box>
                }
              />
              <Tab
                value="2"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      color:
                        theme.palette.mode === "dark"
                          ? value === "2"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Work Requirements
                    </Box>
                  </Box>
                }
              />
              <Tab
                value="3"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      color:
                        theme.palette.mode === "dark"
                          ? value === "3"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Attachments
                    </Box>
                  </Box>
                }
              />
              <Tab
                value="4"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      color:
                        theme.palette.mode === "dark"
                          ? value === "4"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Notes
                    </Box>
                  </Box>
                }
              />
              <Tab
                value="6"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      color:
                        theme.palette.mode === "dark"
                          ? value === "6"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Linked Records
                    </Box>
                  </Box>
                }
              />
              <Tab
                value="7"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      color:
                        theme.palette.mode === "dark"
                          ? value === "7"
                            ? "#fff"
                            : "inherit"
                          : "inherit",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Assignees
                    </Box>
                  </Box>
                }
              />
            </TabList>
            <TabPanel value="1" sx={{ px: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: { md: 8, xs: 0 },
                  flexDirection: { md: "row", xs: "column" },
                }}
              >
                <Box
                  sx={{
                    width: { md: "30%", xs: "100%" },
                    maxWidth: { md: "30%", xs: "100%" },
                  }}
                >
                  <Grid container spacing={{ xs: 3, sm: 5 }}>
                    <Grid item xs={12}>
                      <Divider
                        sx={{
                          color: "text.disabled",
                          "& .MuiDivider-wrapper": { px: 6 },
                          fontSize: theme.typography.body2.fontSize,
                          my: (theme) => `${theme.spacing(3)} !important`,
                        }}
                      >
                        General Information
                      </Divider>
                    </Grid>
                     <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          label="Request Title"
                          disabled={!rules.canEdit}
                          required
                          placeholder="Enter a brief Request title"
                          error={!!requestErrors.requestTitle}
                          helperText={requestErrors.requestTitle}
                          onChange={(e) => {
                            setServiceRequest((prevState) => ({
                              ...prevState,
                              requestTitle: e.target.value,
                            }));
                          }}
                          value={serviceRequest?.requestTitle}
                        />
                      </Grid>

                    {/* Add Description Field */}
                    <Grid item xs={12}>
                      <CustomTextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={2}
                        placeholder="Add a description..."
                        onChange={(e) => {
                          setServiceRequest((prevState) => ({
                            ...prevState,
                            reqDescription: e.target.value,
                          }));
                        }}
                        value={serviceRequest?.reqDescription}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormLabel
                        sx={{
                          fontSize: "13px",
                          color: "text.primary",
                          mb: "4px",
                        }}
                      >
                        Customer Name{" "}
                        <span style={{ color: theme.palette.error.main }}>
                          *
                        </span>
                      </FormLabel>
                      <Box
                        sx={{
                          border: `1px solid rgba(47, 43, 61, 0.2)`,
                          p: 2,
                          borderRadius: 1,
                          cursor: "default",
                          backgroundColor: "action.disabledBackground",
                        }}
                      >
                        <Typography variant="body1">
                          {customer?.customerType === "Individual"
                            ? `${customer?.firstName} ${customer?.lastName}`
                            : customer?.customerType === "Business"
                            ? customer?.companyName
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                 <Grid item xs={12}>
                  <CustomAutocomplete
                    fullWidth
                    required
                    disabled={!customer?.customerID}
                    options={[
                      { jobsiteID: "create-new", jobsiteName: "+ Create New Jobsite", isCreateOption: true },
                      ...((allJobsites || [])
                        .filter(option => option.jobsiteName)
                        .sort((a, b) => a.jobsiteName.localeCompare(b.jobsiteName)))
                    ]}
                    getOptionLabel={(option) => option.jobsiteName || ""}
                    value={
                      allJobsites?.find(
                        (jobsite) => jobsite.jobsiteID === serviceRequest?.jobSiteID
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      if (newValue?.isCreateOption) {
                        if (customer?.customerID) {
                          handleOpenNewJobsiteDlg();
                        }
                      } else if (newValue) {
                        handleJobsiteDropdownChange(newValue.jobsiteID);
                      }
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.jobsiteID === value.jobsiteID
                    }
                    filterOptions={(options, params) => {
                      const filtered = options
                        .filter((option) => {
                          if (option.isCreateOption) return true;
                          const jobsiteName = option.jobsiteName || "";
                          return jobsiteName.toLowerCase().includes(params.inputValue.toLowerCase());
                        })
                        .filter(option => option.isCreateOption || option.jobsiteName);
                      return filtered;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span style={{ 
                          fontWeight: option.isCreateOption ? 'bold' : 'normal',
                          color: option.isCreateOption && !customer?.customerID ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                        }}>
                          {option.jobsiteName}
                        </span>
                      </li>
                    )}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Jobsite"
                        required
                        placeholder="Select Jobsite"
                        error={!!requestErrors.jobSiteID}
                        helperText={requestErrors.jobSiteID}
                      />
                    )}
                  />
                </Grid>
               <Grid item xs={12}>
                      <CustomAutocomplete
                        fullWidth
                        required
                        disabled={!rules.canEdit}
                        id="jobsite-contact-autocomplete"
                        options={[
                          { id: "create-new", contact: "+ Create New Contact", isCreateOption: true },
                          ...((contacts || []).sort((a, b) => (a.contact || "").localeCompare(b.contact || "")))
                        ]}
                        getOptionLabel={(option) => option.contact || ""}
                        value={
                          contacts?.find(
                            (contact) => contact.id === serviceRequest?.jobsiteContactID
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          if (newValue?.isCreateOption) {
                            handleOpenNewContactDlg();
                          } else if (newValue) {
                            handleContactDropdownChange(newValue.id);
                          }
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        filterOptions={(options, params) => {
                          return options.filter((option) => {
                            if (option.isCreateOption) return true;
                            return (option.contact || "").toLowerCase().includes(params.inputValue.toLowerCase());
                          });
                        }}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <span style={{ 
                              fontWeight: option.isCreateOption ? 'bold' : 'normal',
                            }}>
                              {option.contact}
                            </span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            label="Jobsite Contact"
                            required
                            placeholder="Select Jobsite Contact"
                            error={!!requestErrors.jobsiteContactID}
                            helperText={requestErrors.jobsiteContactID}
                          />
                        )}
                      />
                    </Grid>             
                    <Grid item xs={12}>               
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1,mb: 2 }}>
                      <Button  sx={{position: 'absolute', bottom: 10, right: 20}} variant="contained" color="main"  onClick={() => handleSaveServiceRequest()} disabled={btnLoading || !isChanged} >
                          {btnLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                      </Button>              
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box
                  sx={{
                    width: { md: "70%", xs: "100%" },
                    maxWidth: { md: "70%", xs: "100%" },
                    mt: { xs: 0, md: 0 },
                  }}
                >
                  <Grid container spacing={3} sx={{ width: "100%" }}>
                    <Grid item xs={12}>
                      <Divider
                        sx={{
                          color: "text.disabled",
                          "& .MuiDivider-wrapper": { px: { md: 6, xs: 0 } },
                          fontSize: theme.typography.body2.fontSize,
                          my: (theme) => `${theme.spacing(3)} !important`,
                        }}
                      >
                        Availability Details
                      </Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <FormLabel
                          sx={{ fontSize: "13px", color: "text.primary" }}
                        >
                          Preferred Times
                        </FormLabel>
                        <FormControl component="fieldset">
                          {allTimes.map((time, index) => {
                            // Check if "Anytime" is checked
                            const isAnytimeChecked =
                              serviceRequest?.availabilityTime.includes("1");

                            // Determine if the current time should be checked
                            const isChecked =
                              isAnytimeChecked ||
                              serviceRequest?.availabilityTime.includes(
                                Number(
                                  Object.keys(timeMapping).find(
                                    (key) => timeMapping[key] === time
                                  )
                                )
                              );
                            return (
                              <FormControlLabel
                                key={index}
                                control={
                                  <Checkbox
                                    checked={isChecked}
                                    onChange={(e) =>
                                      handleTimeChange(time, e.target.checked)
                                    }
                                    color="success"
                                    disabled={!rules.canEdit}
                                  />
                                }
                                label={time}
                              />
                            );
                          })}
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <FormLabel
                          sx={{
                            fontSize: "13px",
                            color: "text.primary",
                            marginBottom: "4px",
                          }}
                        >
                          Preferred Days
                        </FormLabel>
                        <ToggleButtonGroup value={availabilityDaysArray}>
                          {allDays.map((day, index) => {
                            const dayNumber = dayToNumberMap[day];
                            return (
                              <ToggleButton
                                // disabled={
                                //   !rules?.canEdit ||
                                //   dayNumber === 0 ||
                                //   dayNumber === 6
                                // }
                                key={index}
                                value={day}
                                color={"info"}
                                selected={availabilityDaysArray.includes(
                                  dayNumber
                                )} // Mark selected ones
                                onChange={() => handleDayChange(day)}
                                sx={{
                                  fontSize: { xs: "11px", sm: "13px" },
                                  px: { xs: 1, sm: 2 },
                                  minWidth: { xs: "40px", sm: "60px" },
                                }}
                              >
                                {day}
                              </ToggleButton>
                            );
                          })}
                        </ToggleButtonGroup>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormLabel
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                          height: "100%",
                        }}
                      >
                        Complete Work By
                      </FormLabel>
                      {loading ? (
                        <p></p> // Show a loading indicator while data is being fetched
                      ) : (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            sx={{ width: "100%" }}
                            disabled={!rules.canEdit}
                            value={
                              serviceRequest?.completeWorkBy &&
                              serviceRequest?.completeWorkBy !==
                                "0001-01-01T00:00:00" &&
                              serviceRequest?.completeWorkBy !==
                                "1900-01-01T00:00:00"
                                ? dayjs(serviceRequest.completeWorkBy)
                                : null
                            }
                            onChange={(newValue) => {
                              handleCompleteWorkByDateChange(newValue);
                            }}
                            slotProps={{ textField: { size: "small" } }}
                          />
                        </LocalizationProvider>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Divider
                        sx={{
                          color: "text.disabled",
                          "& .MuiDivider-wrapper": { px: 4 },
                          fontSize: theme.typography.body2.fontSize,
                          my: (theme) => `${theme.spacing(3)} !important`,
                        }}
                      >
                        Additional Details
                      </Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography variant="body1" sx={{ mr: 6 }}>
                          Tags
                        </Typography>
                        <IconButton
                          onClick={() => {
                            handleOpenTagManagementDlg();
                          }}
                          // disabled={!rules.canEdit}
                        >
                          <AddIcon />
                        </IconButton>
                        {serviceRequest?.tags &&
                        serviceRequest.tags.length > 0 ? (
                          serviceRequest.tags.map((tag) => (
                            <CustomChip
                              label={tag.tagName}
                              skin="light"
                              color="info"
                              // disabled={!rules.canEdit}
                              deleteIcon={
                                <Tooltip title="Click to remove tag">
                                  <CloseIcon />
                                </Tooltip>
                              }
                              onDelete={() => handleDeleteTag(tag.tagName)}
                              sx={{
                                cursor: "pointer",
                                mr: 2,
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            No tags
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </TabPanel>
            <TabPanel value="2" sx={{ px: 0 }}>
              <Grid container spacing={5}>
                {workRequirementList && workRequirementList.length === 0 ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent
                          sx={{
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1B2431"
                                : "#FBFBFC",
                            p: (theme) =>
                              `${theme.spacing(9.75, 5, 9.25)} !important`,
                          }}
                        >
                          <CustomAvatar
                            skin="light"
                            color="info"
                            sx={{ mb: 2.25 }}
                          >
                            <Icon icon="tabler:building" />
                          </CustomAvatar>
                          <Typography variant="h5" sx={{ mb: 2.75 }}>
                            No Bundles added yet
                          </Typography>
                          <Typography sx={{ mb: 6, color: "text.secondary" }}>
                            Add bundles that are required by clicking on “Add
                            Bundle” button
                          </Typography>
                          <Button
                            variant="outlined"
                            color={
                              theme.palette.mode === "dark"
                                ? "secondary"
                                : "primary"
                            }
                            startIcon={<AddIcon color="info" />}
                            onClick={handleOpenBundleDlg}
                            disabled={!rules.canEdit}
                          >
                            Add Bundle
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent
                          sx={{
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1B2431"
                                : "#FBFBFC",
                            p: (theme) =>
                              `${theme.spacing(9.75, 5, 9.25)} !important`,
                          }}
                        >
                          <CustomAvatar
                            skin="light"
                            color="info"
                            sx={{ mb: 2.25 }}
                          >
                            <Icon icon="tabler:building" />
                          </CustomAvatar>
                          <Typography variant="h5" sx={{ mb: 2.75 }}>
                            No Services added yet
                          </Typography>
                          <Typography sx={{ mb: 6, color: "text.secondary" }}>
                            Add services that are required by clicking on “Add
                            Service” button
                          </Typography>
                          <Button
                            variant="outlined"
                            color={
                              theme.palette.mode === "dark"
                                ? "secondary"
                                : "primary"
                            }
                            startIcon={<AddIcon color="info" />}
                            onClick={handleOpenServiceDlg}
                            disabled={!rules.canEdit}
                          >
                            Add Service
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                ) : (
                  <WorkRequirements
                    entity="request"
                    {...WorkRequirementsProps}
                    rules={rules}
                  />
                )}
              </Grid>
            </TabPanel>
            <TabPanel value="3">
              <AttachmentsComponent
                rules={rules}
                request={request}
                requestType={"Request"}
                handleOpenUnsavedChangesDlg={handleOpenUnsavedChangesDlg}
                theme={theme}
                loading={loading}
                AttachmentProps={AttachmentProps}
                openUploadDlg2={openUploadDlg2}
                setOpenUploadDlg2={setOpenUploadDlg2}
              />
            </TabPanel>
            <TabPanel value="4" sx={{ p: 0, pt: 4 }}>
              <Box
                sx={{
                  height: "70vh",
                }}
              >
                <NotesComponent
                  requestID={requestID}
                  requestType={"Request"}
                  customerID={request?.customerID}
                  setLoading={setLoading}
                  rules={rules}
                />
              </Box>
            </TabPanel>
            {/* <TabPanel value="5">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 4,
                }}
              >
                <Typography variant="body1">
                  {sortTimeline ? "Newest First" : "Oldest First"}
                </Typography>
                <IconButton onClick={() => setSortTimeline(!sortTimeline)}>
                  <Icon
                    icon={`tabler:sort-${
                      sortTimeline ? "descending" : "ascending"
                    }`}
                  />
                </IconButton>
              </Box>
              <Timeline sx={{ mb: 6 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 4 }}>
                  Today - Jun 03, 2024
                </Typography>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color="success" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent
                    sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ mr: 6 }}>
                        {<strong>John Smith</strong>} added an{" "}
                        {<strong>Attachment</strong>}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        30 minutes ago
                      </Typography>
                    </Box>
                    <Box
                      sx={{ mt: 2, display: "flex", alignItems: "center" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                      <Icon icon="material-symbols:arrow-right-alt-rounded" />
                      <Typography variant="body2">
                        mylawn_frontyard.png
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color="success" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent
                    sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ mr: 4 }}>
                        {<strong>John Smith</strong>} added a{" "}
                        {<strong>Link</strong>}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        30 minutes ago
                      </Typography>
                    </Box>
                    <Box
                      sx={{ mt: 2, display: "flex", alignItems: "center" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                      <Icon icon="material-symbols:arrow-right-alt-rounded" />
                      <Typography variant="body2">
                        This request is linked with Estimation EST-001
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              </Timeline>
              <Timeline>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 4 }}>
                  Jun 01, 2024
                </Typography>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color="success" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent
                    sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ mr: 6 }}>
                        {<strong>John Smith</strong>} added a{" "}
                        {<strong>Note</strong>}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        03:05 PM
                      </Typography>
                    </Box>
                    <Box
                      sx={{ mt: 2, display: "flex", alignItems: "center" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                      <Icon icon="material-symbols:arrow-right-alt-rounded" />
                      <Typography variant="body2"></Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color="success" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent
                    sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ mr: 4 }}>
                        {<strong>John Smith</strong>} changed the{" "}
                        {<strong>Status</strong>}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        02:20 PM
                      </Typography>
                    </Box>
                    <Box
                      sx={{ mt: 2, display: "flex", alignItems: "center" }}
                    >
                      <CustomChip
                        rounded
                        size="small"
                        skin="light"
                        sx={{ lineHeight: 1 }}
                        color="info" // Use color names: primary, secondary, error, warning, info, success
                        label="IN PROGRESS"
                      />
                      <Icon icon="material-symbols:arrow-right-alt-rounded" />
                      <CustomChip
                        rounded
                        size="small"
                        skin="light"
                        sx={{ lineHeight: 1 }}
                        color="success" // Use color names: primary, secondary, error, warning, info, success
                        label="REVIEWED"
                      />
                    </Box>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color="success" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent
                    sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ mr: 4 }}>
                        {<strong>John Smith</strong>} created the{" "}
                        {<strong>Request</strong>}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        02:00 PM
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              </Timeline>
            </TabPanel> */}
            <TabPanel value="6" sx={{ px: 0 }}>
              <LinkedRecordsComponent
                requestID={requestID}
                customerID={request?.customerID}
                setLoading={setLoading}
                module="detail"
                rules={rules}
                onLinkedEntityFound={setLinkedFromEntityId} 
              />
            </TabPanel>
            <TabPanel value="7" sx={{ px: 0 }}>
              <Grid container spacing={5}>
                <AssignToComponent
                  requestID={requestID}
                  serviceRequest={serviceRequest}
                  requestType={"Request"}
                  setLoading={setLoading}
                  rules={rules}
                  navigate={navigate}
                  handleServiceRequestChange={handleServiceRequestChange}
                />
              </Grid>
            </TabPanel>
          </TabContext>
        </CardContent>
      </Card>

      {/* {mountAssigneeTab && (
        <Box sx={{display: 'none !important'}}>
          <AssignToComponent 
            requestID={requestID}
            serviceRequest={serviceRequest}
            requestType={"Assessment"}
            customerID={serviceRequest?.customerID}
            setLoading={setLoading}
            rules={rules}
            setAssignees={setAssignees}
            assignees={assignees}
          />
        </Box>
      )} */}

      {/* Schedule Assessment Dialog */}
      {/* <ScheduleAssessment 
        open={openScheduleAst}
        handleClose={handleCloseScheduleAstDlg}
        entity='convert'
        requestID={requestID}
        // serviceRequest={serviceRequest}
        requestType={"Assessment"}
        setLoading={setLoading}
      /> */}

      {/* Assign To Dialog */}
      {/* <AssignToDialog 
        open={openAssignTo}
        handleClose={handleCloseAssignToDlg}
        requestID={requestID}
        serviceRequest={serviceRequest}
        requestType={"Request"}
        customerID={serviceRequest?.customerID}
        setLoading={setLoading}
      /> */}

      {/* Add Customer Dialog */}

      <AddCustomerDialog
        open={openNewCustomerDlg}
        setOpenNewCustomerDlg={setOpenNewCustomerDlg}
        fetchData={fetchData}
        allCustomers={allCustomers}
      />

      <AddJobsiteDialog
        open={openNewJobsiteDlg}
        handleClose={handleCloseNewJobsiteDlg}
        setJobsiteOpen={setOpenNewJobsiteDlg}
        customer={customer}
        fetchContactData={fetchContacts}
        toast={toast}
        jobsite={jobsite}
        handleJobsiteChange={handleJobsiteChange}
        navigate={navigate}
        NProgress={NProgress}
        fetchContacts={fetchContacts}
        fetchJobsites={fetchJobsites}
        contacts={allContacts}
        propertyArea={propertyArea}
        setPropertyArea={setPropertyArea}
        areaMeasurement={areaMeasurement}
        setAreaMeasurement={setAreaMeasurement}
        setLoading={setLoading}
        activeStep={activeStepJobsite}
        setActiveStep={setActiveStepJobsite}
        clearAreaMeasurement={clearAreaMeasurement}
        fetchPropertyArea={fetchPropertyArea}
        propertyAreaList={propertyAreaList}
        clearPropertyArea={clearPropertyArea}
        jobsiteContent={jobsiteContent}
        alertList={alertList}
        setAlertList={setAlertList}
        files={files}
        setFiles={setFiles}
      />

      {/* Add Jobsite Dialog */}

      {/* Add Contact Dialog */}
      <AddContactDialog
        open={openNewContactDlg}
        handleClose={handleCloseContactDialog}
        customer={customer}
        setLoading={setLoading}
        fetchContactData={fetchContacts}
        toast={toast}
        contacts={contacts}
      />

      <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={{ title: "Tag Management" }}
        handleAddTag={handleAddTag}
        handleDeleteTag={handleDeleteTag}
        tags={serviceRequest?.tags}
        tag={Tag}
        setTag={setTag}
      />

      {/* Add Service Dialog */}
      {/* <AddServiceDialog
        open={openAddService}
        serviceContent={serviceContent}
        handleClose={handleCloseServiceDlg}
        handleOpenUploadDlg={handleOpenUploadDlg}
        setFiles = {setUploadFiles}  
        sf = {sf} 
        f = {f}
        navigate= {navigate}
        jobsite = {jobsite}
        serviceRequest={serviceRequest}
        toast={toast}
        fetchWorkRequirements = {fetchWorkRequirements}
        handleOpenNewPropertyAreaDlg = {handleOpenNewPropertyAreaDlg}
        handleDeleteFile={handleDeleteFile}
        {...WorkReqProps}
      /> */}

      {/* <AddBundle 
        open={openAddBundle}
        handleBundleClose={handleCloseBundleDlg}
        handleSave={handleSave}
      />

      <AddService 
        open={openAddService}
        handleServiceClose={handleCloseServiceDlg}
        serviceList={serviceList}
      /> */}

      {/* Attachment Upload Dialog */}

      <AddServiceDialog {...AddServiceProps} />

      <AddBundleDialog {...AddBundleProps} />
      <UnsavedChangesDialog
        open={openUnsavedChangesDlg}
        handleClose={handleCloseUnsavedChangesDlg}
        handleDiscard={handleDiscardChanges}
      />
      {/* <PreAttachment
        Title="Upload Attachment"
        {...AttachmentProps}
      /> */}
      {/* Send Portal Invite Dialog */}

      {/* Send Portal Invite Sent Toast */}      
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default RequestDetail;
